import { Controller } from "@hotwired/stimulus"

// data-controller="tablist"
export default class extends Controller {
  static targets = ["nav", "pane", "navShell"]

  connect() {
    this._onLessonLoaded = (event) => this.update(event)
    this._onTabShown = (event) => {
      const nav = this.primaryNavElement()
      if (!nav?.contains(event.target)) return

      this.syncActiveTabState(nav, event.target)
    }
    this._onResize = () => this.refreshNavCarousel()

    this.element.addEventListener("lesson:loaded", this._onLessonLoaded)
    this.primaryNavElement()?.addEventListener("shown.bs.tab", this._onTabShown)
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    this.element.removeEventListener("lesson:loaded", this._onLessonLoaded)
    this.primaryNavElement()?.removeEventListener("shown.bs.tab", this._onTabShown)
    window.removeEventListener("resize", this._onResize)

    if (this._navCarouselTimer) clearTimeout(this._navCarouselTimer)
    this.destroyNavCarousel()
  }

  update(event) {
    const detail = event.detail
    const tabs = this.buildTabs(detail)

    this.destroyNavCarousel()

    const navElements = Array.from(
      this.element.querySelectorAll("[data-tablist-target='nav']")
    )

    navElements.forEach((nav) => {
      nav.innerHTML = ""
      tabs.forEach((tab) => nav.insertAdjacentHTML("beforeend", tab.html))
    })

    this.syncVideosTab(detail)
    this.ensureActiveTab(detail)
    this.initNavCarousel()
    this.bindNavTabInteractions()
  }

  primaryNavElement() {
    return document.getElementById("lesson-tablist-nav")
      || (this.hasNavTarget ? this.navTarget : null)
  }

  navShellElement() {
    const nav = this.primaryNavElement()
    if (!nav) return this.hasNavShellTarget ? this.navShellTarget : null

    return nav.closest("[data-tablist-target='navShell']")
      || (this.hasNavShellTarget ? this.navShellTarget : null)
  }

  syncVideosTab(detail = {}) {
    const hasVideos = Array.isArray(detail.videos) && detail.videos.length > 0
    const pane = document.getElementById("tab-videos")
    const tab = document.getElementById("tab-videos-tab")

    if (!pane) return

    if (!hasVideos) {
      pane.classList.remove("active", "show")
      tab?.classList.remove("active")
      tab?.setAttribute("aria-selected", "false")
    }
  }

  activateVideosTab(nav) {
    const videosTab = nav.querySelector("#tab-videos-tab")
    if (!videosTab) return false

    this.activateTab(videosTab, nav)
    return true
  }

  buildTabs({ released, videos = [], digital_notebooks = [], pdf_completos = [], pdf_resumidos = [], mapa_mentais = [], audios = [], review_womb_component_lectures = [], description }) {
    const tabs = []

    if (!released) {
      tabs.push(this.tabTemplate("tab-ravi", "Rav", "fa-comments"))
      return tabs
    }

    if (videos.length) {
      tabs.push(this.tabTemplate("tab-videos", "Vídeos", "fa-video-camera"))
    }

    tabs.push(this.tabTemplate("tab-ravi", "Rav", "fa-comments"))

    if (digital_notebooks.length) {
      tabs.push(this.tabTemplate("tab-digital-notebook", "Caderno Digital", "fa-book"))
    }

    if (pdf_completos.length) {
      tabs.push(this.tabTemplate("tab-pdf-completo", "PDF Completo", "fa-file-pdf-o"))
    }

    if (pdf_resumidos.length) {
      tabs.push(this.tabTemplate("tab-pdf-resumido", "PDF Resumido", "fa-file-pdf-o"))
    }

    if (mapa_mentais.length) {
      tabs.push(this.tabTemplate("tab-mapa-mental", "Mapa Mental", "fa-map-marker"))
    }

    if (audios.length) {
      tabs.push(this.tabTemplate("tab-audio", "ÁudioCast", "fa-volume-up"))
    }

    if (review_womb_component_lectures.length) {
      tabs.push(this.tabTemplate("tab-revision", "Aula de Revisão", "fa-refresh"))
    }

    if (description && description.trim().length) {
      tabs.push(this.tabTemplate("tab-observation", "Observação", "fa-eye"))
    }

    return tabs
  }

  tabTemplate(id, label, icon) {
    return {
      id,
      html: `
      <li class="nav-item" data-tab="${id}">
        <a class="nav-link"
           id="${id}-tab"
           data-toggle="tab"
           href="#${id}"
           role="tab"
           aria-controls="${id}"
           aria-selected="false">
          <i class="fa ${icon}" aria-hidden="true"></i>
          ${label}
        </a>
      </li>
    `,
    }
  }

  bindNavTabInteractions() {
    const nav = this.primaryNavElement()
    if (!nav || !window.jQuery) return

    const $nav = window.jQuery(nav)
    $nav.off(".lessonTablist")

    let dragStartX = 0
    let didDrag = false

    $nav.on("mousedown.lessonTablist touchstart.lessonTablist", (event) => {
      const point = event.type === "touchstart" ? event.originalEvent.touches[0] : event
      dragStartX = point?.clientX || 0
      didDrag = false
    })

    $nav.on("mousemove.lessonTablist touchmove.lessonTablist", (event) => {
      const point = event.type === "touchmove" ? event.originalEvent.touches[0] : event
      if (point && Math.abs(point.clientX - dragStartX) > 6) {
        didDrag = true
      }
    })

    $nav.on("click.lessonTablist", ".nav-link", (event) => {
      if (didDrag) {
        didDrag = false
        return
      }

      event.preventDefault()
      this.activateTab(event.currentTarget, nav)
    })
  }

  activateTab(link, nav = this.primaryNavElement()) {
    if (!link || !nav || link.classList.contains("active")) return

    const previous = nav.querySelector(".nav-link.active")

    this.showTabPane(link)
    this.syncActiveTabState(nav, link)

    link.dispatchEvent(new CustomEvent("shown.bs.tab", {
      bubbles: true,
      detail: { relatedTarget: previous },
    }))

    if (link.getAttribute("href") === "#tab-videos") {
      document.querySelector(".main-content-lesson")?.dispatchEvent(
        new CustomEvent("lessons--videos:tab-active")
      )
    }
  }

  showTabPane(link) {
    const tabContent = document.getElementById("lesson-tablist-content")
    const selector = link.getAttribute("href")

    tabContent?.querySelectorAll(".tab-pane").forEach((pane) => {
      pane.classList.remove("active", "show")
    })

    const pane = selector ? document.querySelector(selector) : null
    pane?.classList.add("active", "show")
  }

  syncActiveTabState(nav, activeLink) {
    nav.querySelectorAll(".nav-link").forEach((link) => {
      const isActive = link === activeLink
      link.classList.toggle("active", isActive)
      link.setAttribute("aria-selected", isActive ? "true" : "false")
    })
  }

  unbindNavTabInteractions() {
    const nav = this.primaryNavElement()
    if (!nav || !window.jQuery) return

    window.jQuery(nav).off(".lessonTablist")
  }

  ensureActiveTab(detail = {}) {
    const hasVideos = Array.isArray(detail.videos) && detail.videos.length > 0
    const navElements = Array.from(
      this.element.querySelectorAll("[data-tablist-target='nav']")
    )

    navElements.forEach((nav) => {
      if (hasVideos && this.activateVideosTab(nav)) return

      const activeTab = nav.querySelector(".nav-link.active")
      if (!activeTab) {
        const firstTab = nav.querySelector(".nav-link")
        if (firstTab) this.activateTab(firstTab, nav)
      }
    })
  }

  initNavCarousel() {
    const nav = this.primaryNavElement()
    if (!nav || !window.jQuery?.fn?.slick) return

    const itemCount = nav.querySelectorAll(".nav-item").length
    if (!itemCount) return

    const $nav = window.jQuery(nav)
    const shell = this.navShellElement()

    $nav.slick({
      variableWidth: true,
      infinite: false,
      arrows: true,
      dots: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      swipe: true,
      swipeToSlide: true,
      draggable: true,
      touchThreshold: 8,
      focusOnSelect: false,
      accessibility: false,
      appendArrows: shell ? window.jQuery(shell) : $nav,
      prevArrow: '<button type="button" class="slick-arrow slick-prev lesson-tablist__arrow lesson-tablist__arrow--prev" aria-label="Abas anteriores"><i class="fa-solid fa-chevron-left" aria-hidden="true"></i></button>',
      nextArrow: '<button type="button" class="slick-arrow slick-next lesson-tablist__arrow lesson-tablist__arrow--next" aria-label="Próximas abas"><i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button>',
    })

    $nav.on("setPosition.lessonTablist", () => this.syncNavCarouselState(nav))

    if (this._navCarouselTimer) clearTimeout(this._navCarouselTimer)
    this._navCarouselTimer = setTimeout(() => {
      if ($nav.hasClass("slick-initialized")) {
        $nav.slick("setPosition")
        this.syncNavCarouselState(nav)
      }
    }, 0)
  }

  refreshNavCarousel() {
    const nav = this.primaryNavElement()
    if (!nav || !window.jQuery) return

    const $nav = window.jQuery(nav)
    if (!$nav.hasClass("slick-initialized")) return

    $nav.slick("setPosition")
    this.syncNavCarouselState(nav)
  }

  destroyNavCarousel() {
    const nav = this.primaryNavElement()
    if (!nav || !window.jQuery) return

    this.unbindNavTabInteractions()

    const $nav = window.jQuery(nav)
    $nav.off("setPosition.lessonTablist")

    if ($nav.hasClass("slick-initialized")) {
      try {
        $nav.slick("unslick")
      } catch (_error) {
        nav.querySelectorAll(".slick-track, .slick-list, .slick-dots").forEach((node) => {
          node.remove()
        })
        nav.classList.remove("slick-initialized", "slick-slider")
      }
    }

    this.navShellElement()?.querySelectorAll(".lesson-tablist__arrow").forEach((arrow) => arrow.remove())

    $nav.removeData("slick")
    this.navShellElement()?.classList.remove(
      "lesson-tablist__nav-shell--overflow",
      "lesson-tablist__nav-shell--static"
    )
  }

  syncNavCarouselState(nav) {
    const shell = this.navShellElement()
    if (!shell) return

    const track = nav.querySelector(".slick-track")
    const list = nav.querySelector(".slick-list")
    const overflows = Boolean(track && list && track.scrollWidth > list.clientWidth + 2)

    shell.classList.toggle("lesson-tablist__nav-shell--overflow", overflows)
    shell.classList.toggle("lesson-tablist__nav-shell--static", !overflows)
  }
}
